import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DndContext, DragOverlay, useDroppable, } from '@dnd-kit/core';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Masonry from 'react-masonry-css';
import { useDraggable, useScrollPagination } from '../../hooks';
import { Image } from '../../ui/Image';
import { Text } from '../../ui/Text';
import { AssetCard, TextCard, sourceFallback, } from '../AssetCard';
import { ImageCard } from '../Card';
import { Portal } from '../Portal';
import { SVGCard } from '../SVGCard';
import { SkeletonCard } from '../SkeletonCard';
import { StyledDroppableArea, StyledWrapper } from './Grid.style';
export function DroppableArea({ isVisible }) {
    const { setNodeRef: droppableRef } = useDroppable({
        id: 'droppable',
        data: {
            accepts: 'canvas',
        },
    });
    return _jsx(StyledDroppableArea, { ref: droppableRef, isVisible: isVisible });
}
export function Grid({ cardType = 'asset', assetType = 'image', items, actionButtons, onClick = () => {
    return;
}, onDragging = () => {
    return;
}, options = {
    title: true,
}, workspaceRef, columns = 2, draggable = true, isQa, getPaginationItems, }) {
    var _a;
    const overlayElementRef = useRef(null);
    const onDragOver = useCallback((event) => {
        var _a, _b;
        const { activatorEvent, active, delta } = event;
        const mouseEvent = activatorEvent;
        if ((_a = active === null || active === void 0 ? void 0 : active.data) === null || _a === void 0 ? void 0 : _a.current) {
            onClick({
                action: 'drop',
                source: Array.isArray(active.data.current.source)
                    ? active.data.current.source[0]
                    : active.data.current.source,
                details: Object.assign(Object.assign({}, active.data.current.details), { rect: (_b = overlayElementRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect(), delta: delta, mousePosition: {
                        x: mouseEvent.x,
                        y: mouseEvent.y,
                    } }),
            });
        }
    }, [onClick, overlayElementRef] // Add overlayElementRef to the dependency array
    );
    const onDragMove = useCallback((event) => {
        var _a;
        const { activatorEvent, active, delta } = event;
        const mouseEvent = activatorEvent;
        if ((_a = active === null || active === void 0 ? void 0 : active.data) === null || _a === void 0 ? void 0 : _a.current) {
            onDragging({
                action: 'dragging',
                source: Array.isArray(active.data.current.source)
                    ? active.data.current.source[0]
                    : active.data.current.source,
                details: Object.assign(Object.assign({}, active.data.current.details), { delta: delta, mousePosition: {
                        x: mouseEvent.x,
                        y: mouseEvent.y,
                    } }),
            });
        }
    }, [onDragging]);
    const { activeItem, isDragging, sensors, handleDragStart, handleDragEnd, handleDragMove, } = useDraggable({ onDragOver, onDragMove });
    useEffect(() => {
        if (isDragging) {
            onClick({
                action: 'start-dragging',
                source: '',
                details: {},
            });
        }
        else {
            onClick({
                action: 'stop-dragging',
                source: '',
                details: {},
            });
        }
    }, [isDragging, onClick]);
    const isActiveItemText = ((_a = activeItem === null || activeItem === void 0 ? void 0 : activeItem.details) === null || _a === void 0 ? void 0 : _a.type) === 'Text';
    const Card = useMemo(() => {
        switch (cardType) {
            case 'text':
                return TextCard;
            case 'svg':
                return SVGCard;
            case 'image':
                return ImageCard;
            case 'asset':
            default:
                return AssetCard;
        }
    }, [cardType]);
    const lastCardRef = useRef(null);
    const { displayItems, allLoaded } = useScrollPagination({
        items,
        getPaginationItems,
        lastCardRef,
    });
    const overlaySource = useMemo(() => {
        var _a;
        if (!activeItem)
            return '';
        if (assetType !== 'video') {
            return Array.isArray(activeItem.source)
                ? activeItem.source[0]
                : activeItem.source;
        }
        const data = (_a = activeItem.details) === null || _a === void 0 ? void 0 : _a.data;
        if (data &&
            typeof data === 'object' &&
            !Array.isArray(data) &&
            'selected_thumbnail' in data) {
            return data.selected_thumbnail;
        }
        return '';
    }, [activeItem, assetType]);
    return (_jsxs(DndContext, { sensors: sensors, onDragStart: handleDragStart, onDragEnd: handleDragEnd, onDragMove: handleDragMove, autoScroll: false, children: [_jsx(StyledWrapper, { children: _jsxs(Masonry, { breakpointCols: columns, className: "masonry-asset-grid", columnClassName: "masonry-asset-grid-column", children: [displayItems.map((item, index) => (_jsx(Card, { ref: index === displayItems.length - 1 ? lastCardRef : null, source: item.source, title: options.title ? item.title : undefined, data: item.data, details: item.details, actionButtons: actionButtons, draggable: draggable, onClick: onClick, isQa: isQa, icon: item.icon, variant: item.variant }, `grid-${assetType}-${index}-${item.source
                            .toString()
                            .replace(/\s/g, '-')
                            .toLocaleLowerCase()}`))), !allLoaded &&
                            Array.from({ length: 20 }).map((_, index) => (_jsx(SkeletonCard, {}, `skeleton-${index}`)))] }) }), draggable && (_jsxs(Portal, { children: [_jsx(DragOverlay, { dropAnimation: null, children: (activeItem === null || activeItem === void 0 ? void 0 : activeItem.source) ? (overlaySource.startsWith('http') && !isActiveItemText ? (_jsx(Image, { source: overlaySource, sourceFallback: sourceFallback, rounded: true, id: "react-drag-overlay-element", ref: overlayElementRef })) : (_jsx(Text, { size: "Body", id: "react-drag-overlay-element", ref: overlayElementRef, children: Array.isArray(activeItem.source)
                                ? activeItem.source[0]
                                : activeItem.source }))) : null }), _jsx(DroppableArea, { isVisible: isDragging })] }))] }));
}
//# sourceMappingURL=Grid.js.map